import moment from "moment";

import {
  AmenityAPIResponse,
  AmenityDuration,
  AmenityPrice,
} from "@models/services/amenity";
import { SingleSlot } from "@models/services/booking";

/**
 * Robustly extracts AmenityDuration from various API response formats.
 */
export const getDurationInfo = (
  durations:
    | Record<string, AmenityDuration>
    | AmenityDuration
    | AmenityDuration[],
  duration: number | string
): AmenityDuration | null => {
  if (!durations) return null;

  if (Array.isArray(durations)) {
    return durations.find(d => String(d.duration) === String(duration)) || null;
  }

  const durationKey = `T_${duration}`;
  if (durationKey in durations) {
    return (durations as Record<string, AmenityDuration>)[durationKey];
  }

  if (
    !Array.isArray(durations) &&
    "duration" in durations &&
    "startTime" in durations
  ) {
    const single = durations as AmenityDuration;
    if (Number(single.duration) === Number(duration)) {
      return single;
    }
  }

  return null;
};

/**
 * Robustly extracts AmenityPrice from various API response formats.
 */
export const getPriceInfo = (
  prices: Record<string, AmenityPrice> | AmenityPrice | AmenityPrice[],
  duration: number | string
): AmenityPrice | null => {
  if (!prices) return null;

  if (Array.isArray(prices)) {
    return prices.find(p => String(p.duration) === String(duration)) || null;
  }

  const durationKey = `T_${duration}`;
  if (durationKey in prices) {
    return (prices as Record<string, AmenityPrice>)[durationKey];
  }

  if (!Array.isArray(prices) && "duration" in prices && "basePrice" in prices) {
    const single = prices as AmenityPrice;
    if (Number(single.duration) === Number(duration)) {
      return single;
    }
  }

  return null;
};

/**
 * Checks if an amenity is disabled for the selected resource, duration, and time.
 */
export const checkIsAmenityDisabled = (
  amenity: AmenityAPIResponse,
  selectedSlot: SingleSlot,
  selectedTime: string,
  _selectedDate?: string
): boolean => {
  const resourceId = selectedSlot?.resourceId;
  const duration = selectedSlot?.slot?.slot;
  if (!resourceId || !duration) return true;

  const resourceData = amenity.resources?.[resourceId];
  if (!resourceData) return true;

  const durationInfo = getDurationInfo(resourceData.durations, duration);
  if (!durationInfo) return true;

  const { endTime, startTime } = durationInfo;

  // Ensure selectedTime is in HH:mm format for reliable comparison
  const timeToCompare = selectedTime.includes(":")
    ? moment(selectedTime, [moment.ISO_8601, "HH:mm"]).format("HH:mm")
    : selectedTime;

  if (!timeToCompare || !startTime || !endTime) return false;

  const formatTime = (timeStr: string) => {
    if (!timeStr) return "";
    const parts = timeStr.split(":");
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
    }
    return timeStr.substring(0, 5);
  };

  const start = formatTime(startTime);
  let end = formatTime(endTime);

  if (end === "00:00") {
    end = "24:00";
  }

  const timeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const startMinutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);
  const bookingStartMinutes = timeToMinutes(timeToCompare);
  const bookingEndMinutes = bookingStartMinutes + Number(duration);

  // 2. Availability Time Check
  if (bookingStartMinutes < startMinutes || bookingEndMinutes > endMinutes)
    return true;
  return false;
};

/**
 * Checks if an amenity is applicable for the selected resource and duration.
 */
export const isAmenityApplicable = (
  amenity: AmenityAPIResponse,
  selectedSlot: SingleSlot
): boolean => {
  const resourceId = selectedSlot?.resourceId;
  const duration = selectedSlot?.slot?.slot;
  if (!resourceId || !duration) return false;

  const resourceData = amenity.resources?.[resourceId];
  if (!resourceData) return false;

  const hasDuration = !!getDurationInfo(resourceData.durations, duration);
  const hasPrice = !!getPriceInfo(resourceData.prices, duration);

  return hasDuration && hasPrice;
};
